import React, { useState } from 'react';
import { createEvent } from '../api';
import Tooltip from '../components/Tooltip';

export default function CreateEvent() {
  const [form, setForm] = useState({
    name: '',
    description: '',
    organizer_name: '',
    organizer_email: '',
    slots_needed: 2,
    reminder_3day: false,
    reminder_7day: false,
    access_code: '',
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  function update(key, val) {
    setForm(f => ({ ...f, [key]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await createEvent({
        ...form,
        slots_needed: parseInt(form.slots_needed, 10),
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getShareUrl() {
    return `${window.location.origin}/event/${result.id}`;
  }

  function getGoUrl() {
    const base = `/event/${result.id}`;
    if (form.organizer_name.trim()) {
      return `${base}?name=${encodeURIComponent(form.organizer_name.trim())}`;
    }
    return base;
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = getShareUrl();
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  if (result) {
    return (
      <div className="container" style={{ marginTop: '60px' }}>
        <div className="card card-glow fade-in" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✓</div>
          <h2 style={{ marginBottom: '8px', fontSize: '1.5rem' }}>Event Created</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
            Share this link with your participants
          </p>
          <div className="copy-area" style={{ marginBottom: '24px' }}>
            <code>{getShareUrl()}</code>
            <button className="btn-primary" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            You'll receive an email at <strong style={{ color: 'var(--accent-light)' }}>{form.organizer_email}</strong> once
            all {form.slots_needed} participants have responded.
          </p>
          <a href={getGoUrl()} style={{ display: 'inline-block', marginTop: '20px', textDecoration: 'none' }}>
            <button className="btn-secondary" style={{ padding: '12px 28px', fontSize: '0.95rem' }}>
              Go to Event →
            </button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '48px' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }} className="fade-in">
        <h1 style={{ fontSize: '2.2rem', fontWeight: 700, marginBottom: '12px' }}>
          Find the perfect time
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto' }}>
          Create an event, share the link, and we'll figure out when everyone's free. No accounts needed.
        </p>
      </div>

      <div className="card fade-in fade-in-delay-1">
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="event-name">Event Name <Tooltip text="Give your event a name your participants will recognize, like 'Team Lunch' or 'Study Group'" /></label>
            <input
              id="event-name"
              type="text"
              placeholder="e.g. Team Offsite, Birthday Dinner"
              value={form.name}
              onChange={e => update('name', e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="event-desc">Description <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted)' }}>(optional)</span> <Tooltip text="Extra context for participants — location, purpose, what to prepare, etc." /></label>
            <textarea
              id="event-desc"
              rows={2}
              placeholder="Add any details your participants should know..."
              value={form.description}
              onChange={e => update('description', e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="field">
              <label htmlFor="org-name">Your Name <Tooltip text="Your name as the event organizer — participants will see who created the event" /></label>
              <input
                id="org-name"
                type="text"
                placeholder="Your name"
                value={form.organizer_name}
                onChange={e => update('organizer_name', e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="org-email">Your Email <Tooltip text="We'll send the event link and final results to this address" /></label>
              <input
                id="org-email"
                type="email"
                placeholder="you@example.com"
                value={form.organizer_email}
                onChange={e => update('organizer_email', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="slots">Number of Participants <Tooltip text="How many people you're waiting on. Results are calculated once this many people have responded." /></label>
            <input
              id="slots"
              type="number"
              min="1"
              max="50"
              value={form.slots_needed}
              onChange={e => update('slots_needed', e.target.value)}
              required
              style={{ maxWidth: '120px' }}
            />
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '4px' }}>
              How many people need to fill in their availability
            </p>
          </div>

          <div className="field">
            <label>Email Reminders <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted)' }}>(optional)</span> <Tooltip text="We'll email you a reminder if not everyone has responded by the selected timeframe, so you can follow up" /></label>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
              Get a reminder if not everyone has responded
            </p>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <label htmlFor="reminder-3day" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: '0.9rem', fontWeight: 400, color: 'var(--text-primary)' }}>
                <input
                  id="reminder-3day"
                  type="checkbox"
                  checked={form.reminder_3day}
                  onChange={e => update('reminder_3day', e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--accent)', cursor: 'pointer' }}
                />
                After 3 days
              </label>
              <label htmlFor="reminder-7day" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: '0.9rem', fontWeight: 400, color: 'var(--text-primary)' }}>
                <input
                  id="reminder-7day"
                  type="checkbox"
                  checked={form.reminder_7day}
                  onChange={e => update('reminder_7day', e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--accent)', cursor: 'pointer' }}
                />
                After 7 days
              </label>
            </div>
          </div>

          <div className="field">
            <label htmlFor="access-code">Access Code <Tooltip text="Enter the code you were given to create events on this site" /></label>
            <input
              id="access-code"
              type="password"
              placeholder="Enter access code"
              value={form.access_code}
              onChange={e => update('access_code', e.target.value)}
              required
              autoComplete="off"
              style={{ maxWidth: '240px' }}
            />
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', padding: '12px', background: 'rgba(225, 112, 85, 0.1)', borderRadius: 'var(--radius-sm)', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            style={{ width: '100%', padding: '14px', fontSize: '1rem' }}
          >
            {loading ? 'Creating...' : 'Create Event & Get Link'}
          </button>
        </form>
      </div>

      <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--text-muted)', fontSize: '0.85rem' }} className="fade-in fade-in-delay-3">
        <p>
          <span style={{ marginRight: '16px' }}>🔒 No login required</span>
          <span style={{ marginRight: '16px' }}>📧 Results emailed automatically</span>
          <span>⚡ Free & instant</span>
        </p>
      </div>
    </div>
  );
}
